import * as Sentry from '@sentry/nextjs';

import useToast from '~hooks/useToast';
import { isStatusResultError } from '~utils/https/HttpClientFetch';

import type { StatusResult } from '~models/CommonDto';
import type { Options as ToastOptions } from '~utils/commonToast';

export const DEFAULT_ERROR_MESSAGE = '잠시 후 다시 시도해 주세요';
export const UNKNOWN_ERROR_MESSAGE = '알 수 없는 에러가 발생했어요. 잠시 후 다시 시도해 주세요.';
const TIME_OUT_ERROR_MESSAGE =
  '열차 운행업체와 연결이 원활하지 않아요.\n잠시 후 다시 시도해 주세요.';

// GW 타임아웃 에러
export const GATEWAY_TIMEOUT_ERROR = 'gateway.biz.error';
// 레일유럽에서 검색 결과가 정말로 없는 경우 에러
export const NOT_FOUND_ERROR_CODE_DUE_TO_EXPIRED_SEARCH_ID =
  'TRANSPORT_CONNECTOR.OFFERS.UNKNOWN_OFFERS_SEARCH';
// 레일유럽 검색 결과 캐시가 만료된 경우 MRT TRP 서버에서 생성하는 에러
export const SEARCH_RESULT_EXPIRED = 'RAILEUROPE.EXPIRED';
// 레일유럽 전반에 걸쳐 연동사와 연결에 문제가 발생한 경우 발생하는 에러
export const TRANSPORT_CONNECTOR_RAIL_EUROPE_TECHNICAL_ERROR =
  'TRANSPORT_CONNECTOR.RAIL_EUROPE.TECHNICAL_ERROR';

export const OFFERS_NO_SOLUTIONS_FOUND = 'TRANSPORT_CONNECTOR.OFFERS.NO_SOLUTIONS_FOUND';
export const OFFERS_NO_SOLUTIONS_FOUND_MESSAGE =
  '조건에 맞는 열차가 없습니다.\n다른 조건으로 검색해보세요.';

const ERROR_CODES_WITH_POPUP_NOTICE = [
  'TRANSPORT_CONNECTOR.OFFERS.PROVIDER_MAINTENANCE_ERROR',
  'TRANSPORT_CONNECTOR.BOOKING.BOOKING_EXPIRATION',
  'TRANSPORT_CONNECTOR.BOOKING.OFFER_EXPIRATION',
  'TRANSPORT_CONNECTOR.BOOKING.TOO_LATE_TO_BOOK',
  'TRANSPORT_CONNECTOR.BOOKING.ITEM_OFFERS_SEARCHES_HOMOGENEITY',
  'TRANSPORT_CONNECTOR.BOOKING.ITEM_OFFERS_PROVIDERS_HOMOGENEITY',
  'TRANSPORT_CONNECTOR.BOOKING.PROVIDER_ERROR',
  'TRANSPORT_CONNECTOR.BOOKING.PREBOOK_ERROR',
  'TRANSPORT_CONNECTOR.BOOKING.CONFIRM_ERROR',
  'TRANSPORT_CONNECTOR.BOOKING.CONFIRM_FORBIDDEN',
  'TRANSPORT_CONNECTOR.BOOKING.PREBOOK_NO_SEATS_AVAILABLE',
  NOT_FOUND_ERROR_CODE_DUE_TO_EXPIRED_SEARCH_ID,
  SEARCH_RESULT_EXPIRED,
];

function isGatewayBizError(code?: string) {
  return code === GATEWAY_TIMEOUT_ERROR;
}

function isRailEuropeTechnicalError(code?: string) {
  return code === TRANSPORT_CONNECTOR_RAIL_EUROPE_TECHNICAL_ERROR;
}

function getErrorDisplayType(code?: string) {
  if (!code) {
    return 'snackbar';
  }

  return ERROR_CODES_WITH_POPUP_NOTICE.includes(code) ? 'popup' : 'snackbar';
}

function getErrorDialogTitle(code: string) {
  if (getErrorDisplayType(code) === 'popup') {
    switch (code) {
      case 'TRANSPORT_CONNECTOR.OFFERS.PROVIDER_MAINTENANCE_ERROR':
        return '서비스 점검 중 입니다';
      case 'TRANSPORT_CONNECTOR.BOOKING.BOOKING_EXPIRATION':
        return '예약 가능한 시간이 지났어요';
      case 'TRANSPORT_CONNECTOR.BOOKING.OFFER_EXPIRATION':
      case 'TRANSPORT_CONNECTOR.BOOKING.TOO_LATE_TO_BOOK':
      case 'TRANSPORT_CONNECTOR.BOOKING.ITEM_OFFERS_SEARCHES_HOMOGENEITY':
      case NOT_FOUND_ERROR_CODE_DUE_TO_EXPIRED_SEARCH_ID:
      case SEARCH_RESULT_EXPIRED:
        return '검색 결과가 업데이트 되었어요';
      case 'TRANSPORT_CONNECTOR.BOOKING.PREBOOK_ERROR':
      case 'TRANSPORT_CONNECTOR.BOOKING.CONFIRM_ERROR':
      case 'TRANSPORT_CONNECTOR.BOOKING.CONFIRM_FORBIDDEN':
        return '정보 확인이 필요해요';
      default:
        return undefined;
    }
  }
  return undefined;
}

function getErrorDialogButtonText(code: string) {
  if (getErrorDisplayType(code) === 'popup') {
    switch (code) {
      case 'TRANSPORT_CONNECTOR.OFFERS.PROVIDER_MAINTENANCE_ERROR':
      case 'TRANSPORT_CONNECTOR.BOOKING.PREBOOK_ERROR':
      case 'TRANSPORT_CONNECTOR.BOOKING.CONFIRM_ERROR':
      case 'TRANSPORT_CONNECTOR.BOOKING.CONFIRM_FORBIDDEN':
        return '확인';
      case 'TRANSPORT_CONNECTOR.BOOKING.BOOKING_EXPIRATION':
      case 'TRANSPORT_CONNECTOR.BOOKING.OFFER_EXPIRATION':
      case 'TRANSPORT_CONNECTOR.BOOKING.TOO_LATE_TO_BOOK':
      case 'TRANSPORT_CONNECTOR.BOOKING.ITEM_OFFERS_SEARCHES_HOMOGENEITY':
      case NOT_FOUND_ERROR_CODE_DUE_TO_EXPIRED_SEARCH_ID:
      case SEARCH_RESULT_EXPIRED:
        return '재검색하기';

      default:
        return undefined;
    }
  }
  return undefined;
}

function getErrorMessage(error: StatusResult, isMobile: boolean): string {
  const { code, message } = error;

  if (isGatewayBizError(code)) {
    return isMobile ? TIME_OUT_ERROR_MESSAGE : TIME_OUT_ERROR_MESSAGE.replace('\n', ' ');
  }

  switch (code) {
    // 팝업으로 노출되는 에러 메시지
    case 'TRANSPORT_CONNECTOR.OFFERS.PROVIDER_MAINTENANCE_ERROR':
      return '서비스 점검으로 인해 열차권 검색과 예약이 불가능해요. 잠시 후 다시 시도해 주세요.';
    case 'TRANSPORT_CONNECTOR.BOOKING.BOOKING_EXPIRATION':
      return '예약 보장 시간이 만료되었습니다. 다시 검색 후 새로운 열차권을 선택해주세요.';
    case 'TRANSPORT_CONNECTOR.BOOKING.OFFER_EXPIRATION':
    case 'TRANSPORT_CONNECTOR.BOOKING.TOO_LATE_TO_BOOK':
    case 'TRANSPORT_CONNECTOR.BOOKING.ITEM_OFFERS_SEARCHES_HOMOGENEITY':
    case NOT_FOUND_ERROR_CODE_DUE_TO_EXPIRED_SEARCH_ID:
    case SEARCH_RESULT_EXPIRED:
      return '최신 가격과 예약 가능 여부를 확인하기 위해 재검색 해주세요.';
    case 'TRANSPORT_CONNECTOR.BOOKING.ITEM_OFFERS_PROVIDERS_HOMOGENEITY':
    case 'TRANSPORT_CONNECTOR.BOOKING.PROVIDER_ERROR':
      return `고객센터에 문의해 주세요. (에러코드: ${code})`;
    case 'TRANSPORT_CONNECTOR.BOOKING.PREBOOK_ERROR':
    case 'TRANSPORT_CONNECTOR.BOOKING.CONFIRM_ERROR':
    case 'TRANSPORT_CONNECTOR.BOOKING.CONFIRM_FORBIDDEN':
      return `입력한 정보 확인 후 다시 시도해 주세요.${
        isMobile ? '\n' : ' '
      }문제가 계속 된다면 고객센터로 문의해 주세요.`;
    case 'TRANSPORT_CONNECTOR.BOOKING.PREBOOK_NO_SEATS_AVAILABLE':
      return '예약 가능한 좌석이 없습니다.\n다른 기차 혹은 시간대를 선택해 주세요.';

    // 토스트로 노출되는 에러 메시지
    case OFFERS_NO_SOLUTIONS_FOUND:
      return isMobile
        ? OFFERS_NO_SOLUTIONS_FOUND_MESSAGE
        : OFFERS_NO_SOLUTIONS_FOUND_MESSAGE.replace('\n', ' ');
    case 'TRANSPORT_CONNECTOR.OFFERS.YOUTH_CANNOT_TRAVEL_ALONE':
      return '성인 여행자가 포함되어 있는지 확인해 주세요.';
    case TRANSPORT_CONNECTOR_RAIL_EUROPE_TECHNICAL_ERROR:
    case 'TRANSPORT_CONNECTOR.OFFERS.PROVIDER_TIMEOUT':
    case 'TRANSPORT_CONNECTOR.BOOKING.CREATION_ERROR':
    case 'TRANSPORT_CONNECTOR.BOOKING.CONCURRENT_REQUEST':
    case 'TRANSPORT_CONNECTOR.BOOKING.PROVIDER_TIMEOUT':
      return DEFAULT_ERROR_MESSAGE;
    default:
      return message ?? '';
  }
}

export function showErrorFeedback({
  error,
  toast,
  isMobile,
  feedbackOptions,
  onShowErrorDialog,
}: {
  error: unknown;
  toast: ReturnType<typeof useToast>;
  isMobile: boolean;
  feedbackOptions?: Omit<ToastOptions, 'message'> & { errorMessage?: string };
  onShowErrorDialog?: (message: string, title?: string, buttonText?: string) => void;
}) {
  const { errorMessage, ...rest } = feedbackOptions || {};

  if (isStatusResultError(error)) {
    const displayType = getErrorDisplayType(error.code);
    const displayErrorMessage = errorMessage ?? getErrorMessage(error, isMobile);
    const displayTitle = getErrorDialogTitle(error.code);
    const displayButtonText = getErrorDialogButtonText(error.code);

    if (displayType === 'popup') {
      onShowErrorDialog?.(displayErrorMessage, displayTitle, displayButtonText);
    } else {
      toast.error({
        message: errorMessage ?? displayErrorMessage,
        duration: 5000,
        titleLineClamp: 3,
        ...rest,
      });

      if (error.status === 500 && rest?.id !== 'search-error') {
        console.error(error);
        if (!isGatewayBizError(error.code) && !isRailEuropeTechnicalError(error.code)) {
          Sentry.captureException(
            '__requestBody' in error
              ? { ...error, __requestBody: JSON.stringify(error.__requestBody) }
              : error,
          );
        }
      }
    }
  } else {
    toast.error({
      message: UNKNOWN_ERROR_MESSAGE,
      duration: 5000,
      ...rest,
    });

    console.error(error);
  }
}
